import * as React from "react";
import { Link } from "react-router-dom";
import { APP_NAME } from "@/lib/branding";
import { cn } from "@/lib/utils";
import { Brand } from "../Branding/Brand";
import { Container } from "../Layout/Container";
import { Nav, type NavItem } from "./Nav";

export interface HeaderProps {
  /** Navigation links. Omit for a brand-only header. */
  items?: readonly NavItem[];
  /** Right-aligned extras: buttons, status badges, menus. */
  actions?: React.ReactNode;
  /** Replace the default brand lockup. */
  brand?: React.ReactNode;
  /** Stick to the top while scrolling. Default true. */
  sticky?: boolean;
  className?: string;
}

/** Site header: brand on the left (links home), navigation and actions on the right. */
export function Header({ items, actions, brand, sticky = true, className }: HeaderProps) {
  return (
    <header
      className={cn(
        "relative z-ds-sticky border-b border-ds-gold-primary/10 bg-ds-black-primary/70 backdrop-blur-xl",
        sticky && "sticky top-0",
        className,
      )}
    >
      <Container size="wide" className="flex h-16 items-center justify-between gap-4">
        <Link
          to="/"
          aria-label={`${APP_NAME} home`}
          className="min-w-0 rounded-[var(--radius-lg)] focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
        >
          {brand ?? <Brand size="sm" markSize={56} markOffsetY={12} />}
        </Link>
        <div className="flex items-center gap-2">
          {items && items.length > 0 && <Nav items={items} />}
          {actions}
        </div>
      </Container>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-ds-gold-primary/50 to-transparent"
      />
    </header>
  );
}
